import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { testimonials } from '@/mocks/testimonials';

export default function TestimonialsSection() {
  const { t } = useTranslation();
  const [current, setCurrent] = useState(0);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const items = el.querySelectorAll('.reveal-item');
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const target = entry.target as HTMLElement;
            const index = Array.from(items).indexOf(target);
            target.style.transitionDelay = `${index * 100}ms`;
            target.classList.add('reveal-visible');
            observer.unobserve(target);
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -50px 0px' }
    );
    items.forEach((item) => observer.observe(item));
    return () => observer.disconnect();
  }, []);

  const prev = () => setCurrent((c) => (c === 0 ? testimonials.length - 1 : c - 1));
  const next = () => setCurrent((c) => (c === testimonials.length - 1 ? 0 : c + 1));

  return (
    <section ref={sectionRef} className="bg-foreground-950 py-16 md:py-24">
      <div className="w-full px-4 md:px-6">
        <div className="text-center mb-10 md:mb-14">
          <h2 className="reveal-item reveal font-heading text-4xl md:text-6xl lg:text-7xl text-background-50 leading-tight">
            {t('home.testimonials.title')}
          </h2>
        </div>

        <div className="reveal-item reveal max-w-3xl mx-auto">
          <div className="flex items-center gap-2 mb-6">
            <div className="bg-primary-500 rounded-full px-4 py-2 flex items-center gap-2">
              <i className="ri-star-fill text-foreground-950 text-sm"></i>
              <span className="font-body text-sm font-semibold text-foreground-950">
                {t('home.testimonials.rating')}
              </span>
            </div>
          </div>

          <div className="relative min-h-[180px]">
            <div className="transition-opacity duration-300">
              <p className="font-body text-lg md:text-xl text-background-100 leading-relaxed italic">
                "{t(`testimonial.${testimonials[current].key}.text`)}"
              </p>
              <div className="flex items-center gap-3 mt-6">
                <div className="w-10 h-10 rounded-full bg-primary-500 flex items-center justify-center">
                  <span className="font-body text-sm font-bold text-foreground-950">
                    {testimonials[current].avatar}
                  </span>
                </div>
                <div>
                  <p className="font-body text-sm font-semibold text-background-50">
                    {t(`testimonial.${testimonials[current].key}.name`)}
                  </p>
                  <p className="font-body text-xs text-background-400/60">
                    {t(`testimonial.${testimonials[current].key}.location`)}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 mt-8">
            <button
              onClick={prev}
              className="btn-hover-press w-10 h-10 flex items-center justify-center border border-background-700/50 rounded-md hover:bg-background-800/50"
              aria-label={t('home.testimonials.navPrev')}
            >
              <i className="ri-arrow-left-line text-background-100"></i>
            </button>
            <button
              onClick={next}
              className="btn-hover-press w-10 h-10 flex items-center justify-center bg-primary-500 rounded-md hover:bg-primary-600"
              aria-label={t('home.testimonials.navNext')}
            >
              <i className="ri-arrow-right-line text-foreground-950"></i>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}